import { useSyncExternalStore } from 'react';
import { TEXT_PREFERENCES_STORAGE_KEY } from '@/constants/localStorageKeys';

export const TEXT_SIZES = ['small', 'default', 'large', 'extra-large'] as const;
export const FONTS = ['default', 'verdana', 'open-dyslexic'] as const;

export type TextSize = (typeof TEXT_SIZES)[number];
export type Font = (typeof FONTS)[number];

export type TextPreferences = {
  textSize: TextSize;
  font: Font;
  increasedLineSpacing: boolean;
};

export const DEFAULT_TEXT_PREFERENCES: TextPreferences = {
  textSize: 'default',
  font: 'default',
  increasedLineSpacing: false,
};

const pick = <T extends string>(allowed: readonly T[], value: unknown, fallback: T): T =>
  allowed.includes(value as T) ? (value as T) : fallback;

const readStoredPreferences = (): TextPreferences => {
  try {
    const stored = JSON.parse(window.localStorage.getItem(TEXT_PREFERENCES_STORAGE_KEY) ?? '{}');

    return {
      textSize: pick(TEXT_SIZES, stored?.textSize, DEFAULT_TEXT_PREFERENCES.textSize),
      font: pick(FONTS, stored?.font, DEFAULT_TEXT_PREFERENCES.font),
      increasedLineSpacing: stored?.increasedLineSpacing === true,
    };
  } catch {
    // Storage can be unavailable or hold malformed JSON — fall back to the defaults.
    return DEFAULT_TEXT_PREFERENCES;
  }
};

const setOrRemoveAttribute = (name: string, value: string | null) => {
  if (value === null) document.documentElement.removeAttribute(name);
  else document.documentElement.setAttribute(name, value);
};

/** How much wider each font sets the same text than the default font (measured on table text). */
const FONT_WIDTH_SCALE: Record<Font, number> = { default: 1, verdana: 1.1, 'open-dyslexic': 1.45 };

/** Mirrors the preferences onto `<html>`, where `index.css` picks them up. */
const applyToDocument = ({ textSize, font, increasedLineSpacing }: TextPreferences) => {
  setOrRemoveAttribute('data-text-size', textSize === 'default' ? null : textSize);
  setOrRemoveAttribute('data-font', font === 'default' ? null : font);
  setOrRemoveAttribute('data-line-spacing', increasedLineSpacing ? 'increased' : null);
  // For widths set in CSS, e.g. `w-[calc(5rem*var(--font-width-scale))]`.
  document.documentElement.style.setProperty('--font-width-scale', String(FONT_WIDTH_SCALE[font]));
};

/** Module-level so the startup code and the settings hub share one value. Read lazily for tests. */
let currentPreferences: TextPreferences | null = null;
const listeners = new Set<() => void>();

const getTextPreferences = (): TextPreferences => {
  if (currentPreferences === null) currentPreferences = readStoredPreferences();
  return currentPreferences;
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** Applies the saved preferences; call once before the app renders. */
export const applyStoredTextPreferences = () => applyToDocument(getTextPreferences());

export const setTextPreferences = (changes: Partial<TextPreferences>) => {
  const next = { ...getTextPreferences(), ...changes };
  currentPreferences = next;
  applyToDocument(next);

  try {
    window.localStorage.setItem(TEXT_PREFERENCES_STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Persistence is best-effort; the change still applies for this session.
  }

  listeners.forEach((listener) => listener());
};

export const useTextPreferences = (): TextPreferences =>
  useSyncExternalStore(subscribe, getTextPreferences, getTextPreferences);

const DEFAULT_ROOT_FONT_SIZE_PX = 16;

const getRootFontSizePx = () =>
  parseFloat(window.getComputedStyle(document.documentElement).fontSize) || DEFAULT_ROOT_FONT_SIZE_PX;

/** Size of 1rem in px, for code that has to lay out in px (e.g. virtualised rows). */
export const useRootFontSizePx = (): number =>
  useSyncExternalStore(subscribe, getRootFontSizePx, () => DEFAULT_ROOT_FONT_SIZE_PX);

/** A size designed in px at the default text size, as rem so it follows the user's text size. */
export const pxToRem = (px: number) => `${px / DEFAULT_ROOT_FONT_SIZE_PX}rem`;

/** How much the user's text size scales rem-based layout, e.g. 1.5 at extra large. */
export const useRemScale = (): number => useRootFontSizePx() / DEFAULT_ROOT_FONT_SIZE_PX;

/** Widens boxes sized for one line of text, such as table columns, for the user's font. */
export const useFontWidthScale = (): number => FONT_WIDTH_SCALE[useTextPreferences().font];
